// src/utils/leadTracker.js
// Pure, Firebase-free engine for the Lead Tracker (Settings tab). Derives
// tracker rows and metrics from live pipeline + deal + contact data.
// Metric formulas replicate the coach's workbook EXACTLY, including its quirk
// that "APPT TO CONTRACT %" divides contracts by LEADS (not by appointments) —
// the exported workbook must match the coach's format, so do not "fix" it.
import { normalizeStage } from "./buyerLeadStages";
import { resolveSystemSource, leadSourceLabel } from "./leadSourceLabel";

export const TRACKER_BUCKETS = [
  { code: "CCR", label: "Current Client Referral" },
  { code: "PC", label: "Past Client" },
  { code: "PR", label: "Past Client Referral" },
  { code: "PF", label: "Personal Friend" },
  { code: "RLTR", label: "Realtor" },
  { code: "AD", label: "Advertisement" },
  { code: "BUS", label: "Business Partner" },
  { code: "BLDR", label: "Builder" },
];
export const BUCKET_CODES = TRACKER_BUCKETS.map((b) => b.code);
export const UNASSIGNED_BUCKET = "UNASSIGNED";

// The workbook spells September "SEPT" (4 letters) — keep identical.
export const MONTH_LABELS = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEPT", "OCT", "NOV", "DEC",
];

export const DEFAULT_GOALS = {
  monthlyLeadsGoal: 20,
  closingsGoal: 5,
  leadToApptPct: 0.7,
  apptToContractPct: 0.5,
  contractToClosePct: 0.5,
};

// Buyer: Consultation and beyond count as an appointment. Seller: the two
// appointment stages. Parked seller stages (Deferred/Next Year) derive as
// "call" — the per-row override exists exactly for that case.
const BUYER_APPT_STAGES = [
  "Consultation", "Pre-Approved", "Actively Showing", "Offer Submitted",
];
const SELLER_APPT_STAGES = ["Appt Set", "Listing Appointment"];

export function isConvertedLead(lead) {
  const s = String(lead?.status || "").toLowerCase();
  return s === "converted" || s === "won" || !!lead?.convertedDealId;
}

export function isLostLead(lead) {
  return String(lead?.status || "").toLowerCase() === "lost";
}

// Strict YYYY-MM-DD parses as a LOCAL date (raw new Date("YYYY-MM-DD") is UTC
// midnight = the previous evening in US timezones); everything else falls
// back to new Date. Null on garbage.
export function parseLocalDate(value) {
  if (!value) return null;
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function goalsForYear(settings, year) {
  const saved = (settings?.goalsByYear || {})[String(year)] || {};
  return { ...DEFAULT_GOALS, ...saved };
}

export function bucketForSource(sourceValue, systemSources) {
  if (!sourceValue) return UNASSIGNED_BUCKET;
  const src = resolveSystemSource(sourceValue, systemSources);
  const code = src?.trackerBucket;
  return BUCKET_CODES.includes(code) ? code : UNASSIGNED_BUCKET;
}

// getAlldealforAdmin returns doc data without doc ids — deals are joined on
// String(createdat || id), matching how convertedDealId is written.
export function indexDealsById(deals) {
  const map = {};
  (deals || []).forEach((d) => {
    const key = String(d?.createdat || d?.id || "");
    if (key) map[key] = d;
  });
  return map;
}

function closedInfo(lead, dealsById) {
  if (!lead?.convertedDealId) return null;
  const deal = dealsById[String(lead.convertedDealId)];
  if (!deal) return null;
  if (String(deal.dealStage || "").toLowerCase() !== "closed") return null;
  const dt = parseLocalDate(deal.closingdate);
  if (!dt) return null;
  return { year: dt.getFullYear(), monthIndex: dt.getMonth() };
}

function primaryOf(list, legacy) {
  if (Array.isArray(list) && list.length) {
    const p = list.find((x) => x?.primary) || list[0];
    if (p?.value) return p.value;
  }
  return legacy || "";
}

function deriveCallOrAppt(lead, leadType) {
  if (isConvertedLead(lead)) return "appt";
  const stage =
    leadType === "buyer" ? normalizeStage(lead.stage) : lead.stage;
  const apptStages =
    leadType === "buyer" ? BUYER_APPT_STAGES : SELLER_APPT_STAGES;
  return apptStages.includes(stage) ? "appt" : "call";
}

export function deriveTrackerRows({
  buyerLeads = [],
  listingLeads = [],
  deals = [],
  contactsById = {},
  overridesById = {},
  systemSources = [],
  ownerId,
}) {
  const dealsById = indexDealsById(deals);
  const rows = [];
  const needsAttention = [];

  const push = (lead, leadType) => {
    if (!lead || Number(lead.deletedAt) > 0) return;
    if (String(lead.assignedAgent || "") !== String(ownerId || "")) return;
    const created = Number(lead.createdAt) || 0;
    const createdDate = created ? new Date(created) : null;
    const contact = contactsById[lead.contactId] || null;
    const override = overridesById[lead.id] || {};
    const derived = deriveCallOrAppt(lead, leadType);
    const overridden =
      override.callOrAppt === "call" || override.callOrAppt === "appt";
    const closed = closedInfo(lead, dealsById);
    const sourceValue =
      (leadType === "buyer" ? lead.leadSource : lead.source) || "";
    const row = {
      leadId: lead.id,
      leadType,
      contactId: lead.contactId || "",
      createdAt: created,
      year: createdDate ? createdDate.getFullYear() : null,
      monthIndex: createdDate ? createdDate.getMonth() : null,
      name: lead.contactName || "",
      callOrAppt: overridden ? override.callOrAppt : derived,
      callOrApptOverridden: overridden,
      contract: isConvertedLead(lead),
      preApproved: leadType === "buyer" && lead.preApproval === "yes",
      closedYear: closed ? closed.year : null,
      closedMonthIndex: closed ? closed.monthIndex : null,
      closedLabel: closed ? MONTH_LABELS[closed.monthIndex] : "",
      sourceValue,
      sourceName: leadSourceLabel(sourceValue, systemSources),
      bucket: bucketForSource(sourceValue, systemSources),
      referredByContactId: contact?.referredByContactId || "",
      referredByName: contact?.referredByName || "",
      phone: primaryOf(contact?.phones, contact?.phone),
      email: primaryOf(contact?.emails, contact?.email),
      notes: lead.notes || "",
      status: isConvertedLead(lead)
        ? "won"
        : isLostLead(lead)
          ? "lost"
          : "active",
    };
    if (!created) {
      needsAttention.push(row);
      return;
    }
    rows.push(row);
  };

  buyerLeads.forEach((l) => push(l, "buyer"));
  listingLeads.forEach((l) => push(l, "seller"));
  rows.sort((a, b) => a.createdAt - b.createdAt);
  return { rows, needsAttention };
}

export function rowsForMonth(rows, year, monthIndex) {
  return (rows || []).filter(
    (r) => r.year === year && r.monthIndex === monthIndex,
  );
}

// Workbook math, quirks intact:
//   Lead→Appt %       = appts / leads         (B3 = B13/B9)
//   "Appt→Contract %" = contracts / LEADS     (B17 = B16/B9 — the sheet quirk)
//   Contract→Close %  = closings / leads      (B6 = B20/B9)
//   Closings count in the month the linked deal CLOSED, from any lead month.
export function monthMetrics(rows, year, monthIndex, goals) {
  const monthRows = rowsForMonth(rows, year, monthIndex);
  const leads = monthRows.length;
  const appts = monthRows.filter((r) => r.callOrAppt === "appt").length;
  const contracts = monthRows.filter((r) => r.contract).length;
  const closings = (rows || []).filter(
    (r) => r.closedYear === year && r.closedMonthIndex === monthIndex,
  ).length;
  const pct = (n) => (leads > 0 ? n / leads : null);
  const bucketCounts = { [UNASSIGNED_BUCKET]: 0 };
  BUCKET_CODES.forEach((c) => {
    bucketCounts[c] = 0;
  });
  monthRows.forEach((r) => {
    bucketCounts[r.bucket] = (bucketCounts[r.bucket] || 0) + 1;
  });
  const apptGoal = goals.monthlyLeadsGoal * goals.leadToApptPct;
  return {
    leads,
    appts,
    contracts,
    closings,
    leadToApptPct: pct(appts),
    apptToContractPct: pct(contracts),
    contractToClosePct: pct(closings),
    buyers: monthRows.filter((r) => r.leadType === "buyer").length,
    sellers: monthRows.filter((r) => r.leadType === "seller").length,
    preApproved: monthRows.filter((r) => r.preApproved).length,
    leadsOverUnder: leads - goals.monthlyLeadsGoal,
    apptGoal,
    apptsOverUnder: appts - apptGoal,
    closingsOverUnder: closings - goals.closingsGoal,
    bucketCounts,
  };
}

export function yearMetrics(rows, year, goals) {
  const months = MONTH_LABELS.map((label, mi) => ({
    label,
    ...monthMetrics(rows, year, mi, goals),
  }));
  const sum = (key) => months.reduce((acc, m) => acc + m[key], 0);
  const totals = {
    leads: sum("leads"),
    appts: sum("appts"),
    contracts: sum("contracts"),
    closings: sum("closings"),
    buyers: sum("buyers"),
    sellers: sum("sellers"),
    preApproved: sum("preApproved"),
    leadsGoal: goals.monthlyLeadsGoal * 12,
    closingsGoal: goals.closingsGoal * 12,
  };
  return { months, totals };
}

export function topReferrers(rows, year) {
  const map = {};
  (rows || []).forEach((r) => {
    if (r.year !== year || !r.referredByContactId) return;
    const e = (map[r.referredByContactId] ||= {
      contactId: r.referredByContactId,
      name: r.referredByName || "",
      total: 0,
      converted: 0,
    });
    e.total += 1;
    if (r.contract) e.converted += 1;
  });
  return Object.values(map).sort(
    (a, b) => b.total - a.total || b.converted - a.converted,
  );
}

export function topChannels(rows, year) {
  const map = {};
  (rows || []).forEach((r) => {
    if (r.year !== year || !r.sourceValue) return;
    const e = (map[r.sourceValue] ||= {
      sourceValue: r.sourceValue,
      name: r.sourceName || "",
      bucket: r.bucket,
      total: 0,
      converted: 0,
    });
    e.total += 1;
    if (r.contract) e.converted += 1;
  });
  return Object.values(map).sort(
    (a, b) => b.total - a.total || b.converted - a.converted,
  );
}
